import CustomTransferList from 'Components/CustomTransferList/CustomTransferList'
import { WithLoader } from 'Components/WithLoader'
import { useProductsEdit } from 'Services/ProductManagement/Features/Product/Hooks'
import { useGetProductsQuery } from 'Services/ProductManagement/Features/Product/productApi'
import type { Product } from 'Services/ProductManagement/Features/Product/types'
import {
  getProductsInTariffParamKey,
  getTariffProductIdsField
} from 'Services/ProductManagement/Features/Product/utils'
import { useUpdateTariffProducts } from 'Services/ProductManagement/Features/Tariff/Hooks'
import type { Tariff } from 'Services/ProductManagement/Features/Tariff/types'
import {
  All_PRODUCTS_COUNT_PER_REQUEST,
  All_TARIFF_PRODUCTS_COUNT_PER_REQUEST
} from 'Util/constants'
import getTranslation from 'Util/getTranslation'
import SnackbarUtils from 'Util/Snackbar'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField
} from '@mui/material'
import ListItemText from '@mui/material/ListItemText'
import { useId, useMemo } from 'react'
import { t } from 'ttag'

interface TariffProductsEditProps {
  tariff: Tariff
  handleCancel: () => void
}

function TariffProductsEdit({ tariff, handleCancel }: Readonly<TariffProductsEditProps>) {
  const [updateTariff, { isLoading: isUpdateLoading }] = useUpdateTariffProducts()

  const {
    data: { resources: allProducts } = { resources: [] },
    isFetching: isAllProductsFetching
  } = useGetProductsQuery({
    bypass_pagination: true,
    count: All_PRODUCTS_COUNT_PER_REQUEST,
    has_no_tariff: true
  })
  const {
    data: { resources: allTariffProducts } = { resources: [] },
    isFetching: isAllTariffProductsFetching
  } = useGetProductsQuery({
    [getProductsInTariffParamKey()]: tariff.id,
    bypass_pagination: true,
    count: All_TARIFF_PRODUCTS_COUNT_PER_REQUEST
  })

  const productGroupSelectId = useId()

  const {
    searchText,
    productGroupFilter,
    productGroupOptions,
    isProductGroupParameterGroupLoading,
    handleSearchTextChange,
    handleProductGroupFilterChange,
    filterOptions
  } = useProductsEdit(tariff.pta)

  /**
   * Filters out the products that are already in the selected tariff.
   * Since we are making copies of the products, we can not use the product IDs directly.
   * Instead, we need to compare the product_code of the products.
   *
   * @returns Product[] - Array of products that are not already in the selected tariff.
   */
  const productsWithoutTariffProducts = useMemo(() => {
    return allProducts.filter(
      product =>
        !allTariffProducts.some(
          tariffProduct => tariffProduct.product_code === product.product_code
        )
    )
  }, [allProducts, allTariffProducts])

  /**
   * Handles saving the selected tariff's products.
   *
   * @param {Product[]} selectedItems - The list of products that are currently selected for the tariff.
   */
  const handleSave = (selectedItems: Product[]) => {
    // Keep only items that are not yet in the selected tariff's product IDs
    const tariffProductIdsField = getTariffProductIdsField()
    const newItems = selectedItems.filter(item => !tariff[tariffProductIdsField].includes(item.id))

    // Update the tariff with the newly added products
    updateTariff({ id: tariff.id, added_products: newItems })
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Tariff successfully saved.`)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error saving the tariff.`)
      })
  }

  const renderItemContent = (product: Product) => {
    const labelId = `transfer-list-item-${product.id}-label`
    return (
      <ListItemText
        id={labelId}
        primary={`${getTranslation(product?.translations, 'fi').name}`}
        secondary={product?.product_code}
      />
    )
  }

  const isLoading =
    isAllProductsFetching ||
    isAllTariffProductsFetching ||
    isProductGroupParameterGroupLoading ||
    isUpdateLoading

  return (
    <WithLoader loading={isLoading}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <FormControl sx={{ width: '48%' }}>
          <TextField
            size="small"
            sx={{ backgroundColor: 'background.default' }}
            label={t`Product search`}
            placeholder={t`Search products`}
            value={searchText}
            onChange={handleSearchTextChange}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }
            }}
          />
        </FormControl>
        <FormControl sx={{ width: '48%' }}>
          <InputLabel shrink id={productGroupSelectId}>{t`Product group`}</InputLabel>
          <Select
            name="product_group"
            labelId={productGroupSelectId}
            value={productGroupFilter}
            size="small"
            displayEmpty
            label={t`Product group`}
            onChange={handleProductGroupFilterChange}
            sx={{ backgroundColor: 'background.default' }}
            input={<OutlinedInput notched label={t`Product group`} />}
          >
            {productGroupOptions?.map(({ label, value }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <CustomTransferList<Product>
        leftItemsHeaderText={t`Product listing`}
        rightItemsHeaderText={t`Tariff products`}
        leftItems={productsWithoutTariffProducts}
        rightItems={allTariffProducts}
        renderItemContent={renderItemContent}
        handleSave={handleSave}
        handleCancel={handleCancel}
        allowTransferBack={false}
        compareFn={(item: Product) => item.id}
        filterFn={filterOptions}
      />
    </WithLoader>
  )
}

export default TariffProductsEdit
